// components/media/ImageOptimizer.tsx
'use client';

import Image, { type ImageProps } from 'next/image';
import { useEffect, useMemo, useState } from 'react';

const API_HOSTNAME = (process.env.NEXT_PUBLIC_API_HOSTNAME || 'api.indemenu.com').trim();

function isHttpUrl(v: string): boolean {
  return v.startsWith('https://') || v.startsWith('http://');
}

function isRelativeUrl(v: string): boolean {
  return v.startsWith('/');
}

function isDataOrBlobUrl(v: string): boolean {
  return v.startsWith('data:') || v.startsWith('blob:');
}

function isApiStorageUrl(src: string): boolean {
  try {
    const u = new URL(src);
    if (u.hostname !== API_HOSTNAME) return false;
    return u.pathname.startsWith('/storage/');
  } catch {
    return false;
  }
}

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

type ImageOptimizerProps = Omit<
  ImageProps,
  | 'src'
  | 'alt'
  | 'fill'
  | 'width'
  | 'height'
  | 'quality'
  | 'sizes'
  | 'placeholder'
  | 'blurDataURL'
  | 'priority'
  | 'unoptimized'
  | 'onError'
  | 'onLoad'
  | 'onLoadingComplete'
> & {
  src: ImageProps['src'];
  alt: string;

  /** Wrapper layout */
  fill?: boolean;
  width?: number;
  height?: number;
  aspectRatio?: string; // e.g. "16/9"
  minHeight?: number; // used only in fill mode when aspectRatio is not provided

  /** Styling */
  className?: string; // wrapper classes
  imageClassName?: string; // image element classes
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';

  /** Next/Image tuning */
  sizes?: string;
  quality?: number;
  priority?: boolean;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;

  /** Timeout fix: when true, we bypass Next's optimizer (prevents upstream timeouts). */
  unoptimized?: boolean;

  /** UX */
  showLoadingState?: boolean;
  showErrorState?: boolean;

  /** Callbacks */
  onError?: () => void;
  onLoad?: () => void;
};

/**
 * ImageOptimizer
 * - Prevents Next/Image upstream timeout issues by bypassing optimizer for API storage images.
 * - Uses stable wrapper sizing to avoid layout shift.
 * - Skeleton + error fallback.
 * - Safe src handling (string URLs + StaticImport supported).
 */
export default function ImageOptimizer({
  src,
  alt,

  fill = false,
  width,
  height,
  aspectRatio,
  minHeight,

  className = '',
  imageClassName = '',
  objectFit = 'cover',

  sizes = '100vw',
  quality = 88,
  priority = false,
  placeholder = 'empty',
  blurDataURL,

  unoptimized,
  showLoadingState = true,
  showErrorState = true,

  onError,
  onLoad,

  ...rest
}: ImageOptimizerProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const normalized = useMemo<{
    kind: 'invalid' | 'string' | 'static';
    value: string | ImageProps['src'];
    srcString?: string;
  }>(() => {
    if (!src) return { kind: 'invalid', value: '' };

    if (typeof src !== 'string') {
      // Static import / imported image module: always safe for next/image
      return { kind: 'static', value: src };
    }

    const v = src.trim();
    if (!v) return { kind: 'invalid', value: '' };

    // Allow absolute http(s), relative, data/blob
    if (isHttpUrl(v) || isRelativeUrl(v) || isDataOrBlobUrl(v)) {
      return { kind: 'string', value: v, srcString: v };
    }

    return { kind: 'invalid', value: '' };
  }, [src]);

  const shouldBypassOptimizer = useMemo(() => {
    if (typeof unoptimized === 'boolean') return unoptimized;
    if (normalized.kind !== 'string') return false;

    const s = normalized.srcString || '';
    // Bypass for:
    // - API storage images (your main timeout case)
    // - data/blob (never needs optimization)
    if (isDataOrBlobUrl(s)) return true;
    if (isHttpUrl(s) && isApiStorageUrl(s)) return true;

    return false;
  }, [unoptimized, normalized]);

  const objectFitClass = useMemo(() => {
    switch (objectFit) {
      case 'contain':
        return 'object-contain';
      case 'fill':
        return 'object-fill';
      case 'none':
        return 'object-none';
      case 'scale-down':
        return 'object-scale-down';
      case 'cover':
      default:
        return 'object-cover';
    }
  }, [objectFit]);

  // Reset loading/error state when src changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [normalized.kind, normalized.value]);

  const handleLoaded = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleFailed = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  const wrapperStyle = useMemo<React.CSSProperties>(() => {
    let style: React.CSSProperties = {};
    if (fill) {
      if (aspectRatio) {
        style.aspectRatio = aspectRatio;
      } else {
        // Fixed layout fallback: ensure container never collapses in fill mode.
        // Prefer explicit height if provided; otherwise minHeight (or a safe default).
        const mh = typeof minHeight === 'number' ? minHeight : typeof height === 'number' ? height : 240;
        style.minHeight = mh;
      }
    } else {
      // Fixed mode: reduce CLS by fixing the box dimensions if supplied
      if (typeof width === 'number' && typeof height === 'number') {
        style.width = width;
        style.height = height;
      } else if (aspectRatio && typeof width === 'number') {
        style.width = width;
        style.aspectRatio = aspectRatio;
      } else if (aspectRatio && typeof height === 'number') {
        style.height = height;
        style.aspectRatio = aspectRatio;
      }
    }
    return style;
    }, [fill, aspectRatio, minHeight, width, height]);

  const Wrapper = ({ children }: { children: React.ReactNode }) => (
    <div
      className={cx(
        'relative overflow-hidden rounded-md',
        fill ? 'w-full' : 'inline-block',
        className
      )}
      style={wrapperStyle}
    >
      {children}
      {showLoadingState && isLoading && !hasError ? <Skeleton /> : null}
      {showErrorState && hasError ? <ErrorPlaceholder /> : null}
      <style jsx>{`
        @keyframes indemenu_shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );

  const Skeleton = () => (
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="h-full w-full"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(229,231,235,0.9) 0%, rgba(209,213,219,0.9) 50%, rgba(229,231,235,0.9) 100%)',
          backgroundSize: '400% 100%',
          animation: 'indemenu_shimmer 1.4s ease-in-out infinite',
        }}
      />
    </div>
  );

  const ErrorPlaceholder = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-400 border border-gray-200">
      <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    </div>
  );

  // Invalid src
  if (normalized.kind === 'invalid') {
    return (
      <Wrapper>
        <ErrorPlaceholder />
      </Wrapper>
    );
  }

  // =======================
  // HARD FIX FOR UPSTREAM TIMEOUTS
  // =======================
  // For API storage images, Next's optimizer can timeout server-side even when URL works.
  // We fully bypass Next/Image for those cases and render a native <img>.
  if (shouldBypassOptimizer && normalized.kind === 'string') {
    const s = normalized.srcString!;
    return (
      <Wrapper>
        {fill ? (
          <img
            src={s}
            alt={alt}
            onLoad={handleLoaded}
            onError={handleFailed}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            decoding="async"
            className={cx(
              'absolute inset-0 h-full w-full',
              objectFitClass,
              'transition-all duration-500 ease-out',
              isLoading ? 'opacity-0 scale-[1.02]' : 'opacity-100 scale-100',
              imageClassName
            )}
          />
        ) : (
          <img
            src={s}
            alt={alt}
            width={typeof width === 'number' ? width : 400}
            height={typeof height === 'number' ? height : 400}
            onLoad={handleLoaded}
            onError={handleFailed}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            decoding="async"
            className={cx(
              'h-full w-full',
              objectFitClass,
              'transition-all duration-500 ease-out',
              isLoading ? 'opacity-0 scale-[1.02]' : 'opacity-100 scale-100',
              imageClassName
            )}
          />
        )}
      </Wrapper>
    );
  }

  // =======================
  // Next/Image path (optimized)
  // =======================
  const nextImageSrc = normalized.value as ImageProps['src'];

  return (
    <Wrapper>
      <Image
        src={nextImageSrc}
        alt={alt}
        fill={fill}
        width={!fill ? (typeof width === 'number' ? width : 400) : undefined}
        height={!fill ? (typeof height === 'number' ? height : 400) : undefined}
        sizes={sizes}
        priority={priority}
        quality={quality}
        placeholder={placeholder}
        blurDataURL={blurDataURL}
        unoptimized={false}
        onLoadingComplete={handleLoaded}
        onError={handleFailed}
        className={cx(
          fill ? 'absolute inset-0' : '',
          'h-full w-full',
          objectFitClass,
          'transition-all duration-500 ease-out',
          isLoading ? 'opacity-0 scale-[1.02]' : 'opacity-100 scale-100',
          imageClassName
        )}
        {...rest}
      />
    </Wrapper>
  );
}
